import { ORG_URL_SECTIONS } from '@welynk/types';
import { useUIStore } from '@/store/ui-store';

/**
 * Organization workspaces carry the organization in the URL:
 *   /college/<org-slug>/students     /company/<org-slug>/jobs
 * so a link names exactly which college or company it's about, can be shared
 * or bookmarked, and two tabs can sit in two different organizations.
 *
 * Inside the app, code keeps writing the short form ("/college/students"):
 * OrgAwareRouter fills in the organization of the current tab, and
 * OrgScope turns a short-form URL typed or bookmarked by hand into the full
 * one. The helpers here convert between the two forms.
 */

export type OrgCtx = 'college' | 'company';

/** First path segment of every page in each workspace (must match collegeRoutes/companyRoutes). */
const SECTIONS: Record<OrgCtx, ReadonlySet<string>> = {
  college: new Set(ORG_URL_SECTIONS.college),
  company: new Set(ORG_URL_SECTIONS.company),
};

export function isOrgSection(ctx: OrgCtx, segment: string): boolean {
  return SECTIONS[ctx].has(segment);
}

export interface ParsedOrgPath {
  ctx: OrgCtx;
  /** null when the URL is in the short form (no organization named). */
  slug: string | null;
  /** The page within the workspace, e.g. "/students/invite" ("" for the workspace root). */
  rest: string;
}

export function parseOrgPath(pathname: string): ParsedOrgPath | null {
  const segs = pathname.split('/');
  const ctx = segs[1];
  if (ctx !== 'college' && ctx !== 'company') return null;
  const second = segs[2];
  if (!second) return { ctx, slug: null, rest: '' };
  if (isOrgSection(ctx, second)) return { ctx, slug: null, rest: `/${segs.slice(2).join('/')}` };
  const rest = segs.slice(3).join('/');
  return { ctx, slug: second, rest: rest ? `/${rest}` : '' };
}

/** "/college/nitk/students" → "/college/students": the form page and nav logic compare against. */
export function stripOrgSlug(pathname: string): string {
  const parsed = parseOrgPath(pathname);
  if (!parsed?.slug) return pathname;
  return `/${parsed.ctx}${parsed.rest}`;
}

/** Adds `slug` to a short-form workspace path; anything else is returned unchanged. */
export function withOrgSlug(pathname: string, slug: string): string {
  const parsed = parseOrgPath(pathname);
  if (!parsed || parsed.slug) return pathname;
  return `/${parsed.ctx}/${encodeURIComponent(slug)}${parsed.rest || '/dashboard'}`;
}

/** The URL of a page in a named organization's workspace. */
export function orgUrl(ctx: OrgCtx, slug: string, page = '/dashboard'): string {
  return `/${ctx}/${encodeURIComponent(slug)}${page}`;
}

/**
 * The organization a short-form link in this tab means: the one in the tab's
 * current URL when it's the same kind of workspace, otherwise the one last
 * selected in the workspace switcher.
 */
export function currentOrgSlug(ctx: OrgCtx, currentPathname = window.location.pathname) {
  const here = parseOrgPath(currentPathname);
  if (here?.ctx === ctx && here.slug) return decodeURIComponent(here.slug);
  const { selectedOrgSlug, selectedOrgType } = useUIStore.getState();
  const type = ctx === 'college' ? 'COLLEGE' : 'COMPANY';
  return selectedOrgType === type ? selectedOrgSlug : null;
}

/** A short-form path resolved against this tab's organization (when one is known). */
export function resolveOrgPath(pathname: string, currentPathname?: string): string {
  const parsed = parseOrgPath(pathname);
  if (!parsed || parsed.slug) return pathname;
  const slug = currentOrgSlug(parsed.ctx, currentPathname);
  return slug ? withOrgSlug(pathname, slug) : pathname;
}
